/**
 * ProviderDetailsQualifications Component
 * Feature: 009-provider-ui-pixel-perfect
 * Tasks: T092-T096 - Qualifications tab with badges and expertise
 * 
 * Displays provider's bQuest badges, areas of expertise, specialties,
 * awards & recognitions, and professional associations.
 */

import { CheckCircle2, Award, Users, Shield } from 'lucide-react';
import type { ProviderProfile } from '../types/provider-profile.types';
import { ProviderBadgeCard } from './ProviderBadgeCard';
import { hasArrayData } from '../utils/fieldValidation';

type ProviderDetailsQualificationsProps = {
  provider: ProviderProfile;
};

export const ProviderDetailsQualifications = ({ provider }: ProviderDetailsQualificationsProps) => {
  const {
    badges,
    expertise,
    specialties,
    awardsRecognitions,
    professionalAssociations,
    certificationsArray,
    professionalLicenses,
  } = provider;

  // Combine expertise and specialties for display
  const allExpertiseAreas = [
    ...(expertise || []),
    ...(specialties || []),
  ];

  const hasAwards = hasArrayData(awardsRecognitions);
  const hasAssociations = hasArrayData(professionalAssociations);
  const hasCertifications = hasArrayData(certificationsArray);
  const hasLicenses = hasArrayData(professionalLicenses);
  const hasBadges = hasArrayData(badges);
  const hasExpertise = allExpertiseAreas.length > 0;

  // If no qualifications data at all, don't render anything
  if (!hasBadges && !hasExpertise && !hasCertifications && !hasLicenses && !hasAwards && !hasAssociations) {
    return null;
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Qualifications & Expertise</h2>
        <p className="text-gray-600 mt-2">
          Professional credentials, verifications, and areas of expertise
        </p>
      </div>

      {/* bQuest Badges Section - T093 - Graceful degradation */}
      {hasBadges && (
        <section className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#026857]/10 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#026857]" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">bQuest Badges</h3>
              <p className="text-sm text-gray-600">Verified credentials and achievements</p>
            </div>
          </div>

          <div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
            data-testid="badges-grid"
          >
            {badges.map((badge, index) => (
              <ProviderBadgeCard key={`${badge.badgeName}-${index}`} badge={badge} />
            ))}
          </div>
        </section>
      )}

      {/* Areas of Expertise Section - T094 - Graceful degradation */}
      {hasExpertise && (
        <section className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#378F94]/10 rounded-lg flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-[#378F94]" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Areas of Expertise</h3>
              <p className="text-sm text-gray-600">Specialized skills and service areas</p>
            </div>
          </div>

          <div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
            data-testid="expertise-grid"
          >
            {allExpertiseAreas.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                data-testid={`expertise-item-${item.id}`}
              >
                <CheckCircle2 className="w-5 h-5 text-[#378F94] flex-shrink-0" />
                <span className="text-gray-900 text-sm font-medium">{item.name}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Certifications and Licenses Section */}
      {(hasCertifications || hasLicenses) && (
        <section className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Certifications & Licenses</h3>
              <p className="text-sm text-gray-600">Professional credentials and certifications</p>
            </div>
          </div>

          <div className="space-y-4">
            {hasCertifications && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Certifications</h4>
                <div className="space-y-2">
                  {certificationsArray!.map((cert, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-900 text-sm">{cert}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {hasLicenses && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Professional Licenses</h4>
                <div className="space-y-2">
                  {professionalLicenses!.map((license, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-900 text-sm">{license}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Awards & Recognitions Section - T095 - Graceful degradation */}
      {hasAwards && (
        <section className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
              <Award className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Awards & Recognitions</h3>
              <p className="text-sm text-gray-600">Honors and achievements</p>
            </div>
          </div>

          <div className="space-y-3">
            {awardsRecognitions!.map((award, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-4 bg-amber-50/50 rounded-lg border border-amber-100"
                data-testid={`award-item-${index}`}
              >
                <Award className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <span className="text-gray-900 font-medium">{award}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Professional Associations Section - T096 - Graceful degradation */}
      {hasAssociations && (
        <section className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Professional Associations</h3>
              <p className="text-sm text-gray-600">Memberships and affiliations</p>
            </div>
          </div>

          <div className="space-y-3">
            {professionalAssociations!.map((association, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-4 bg-purple-50/50 rounded-lg border border-purple-100"
                data-testid={`association-item-${index}`}
              >
                <Users className="w-5 h-5 text-purple-600 flex-shrink-0" />
                <span className="text-gray-900 font-medium">{association}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

